import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_URL = "https://eventscons.com/eventbackendnew/api/conferences/calendar";
const CONFERENCE_COLOR = "#b21e35";
const EXHIBITION_COLOR = "#f28c28";

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function toISODateOnly(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildMonthCells(activeDate) {
  const year = activeDate.getFullYear();
  const month = activeDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const startWeekday = firstDayOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const UpcomingCurrentEventsHeading = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeDate, setActiveDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_URL);
        setEvents(res?.data?.data || []);
      } catch (e) {
        console.error("Calendar API failed:", e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCalendar();
  }, []);

  const eventByDate = useMemo(() => {
    const map = new Map();

    (events || []).forEach((ev) => {
      if (!ev?.start_date) return;
      const d = new Date(ev.start_date);
      if (isNaN(d.getTime())) return;

      const key = toISODateOnly(d);
      const existing = map.get(key) || [];

      existing.push({
        id: ev.id,
        title: ev.title,
        type: ev.type,
        start_date: ev.start_date,
      });

      map.set(key, existing);
    });

    return map;
  }, [events]);

  const cells = useMemo(() => buildMonthCells(activeDate), [activeDate]);

  const goPrev = () => setActiveDate((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1));
  const goNext = () => setActiveDate((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1));

  const onDayClick = (dateObj) => {
    const dayEvents = eventByDate.get(toISODateOnly(dateObj));
    if (!dayEvents || dayEvents.length === 0) return;

    const firstEvent = dayEvents[0];

    if (firstEvent.type === "exhibition") {
      navigate(`/one/exhibits/${firstEvent.id}`);
    } else if (firstEvent.type === "conference") {
      navigate(`/conference/details/${firstEvent.id}`);
    }
  };

  const getEventColor = (type) => {
    if (type === "exhibition") return EXHIBITION_COLOR;
    return CONFERENCE_COLOR;
  };

  return (
    <div style={{ marginBottom: "60px" }}>
      <div className="sec-title text-center" style={{ marginBottom: "10px" }}>
        <h2
          className="mainheadingfont"
          style={{ fontSize: "42px", fontWeight: 700, color: "#000" }}
        >
          <span style={{ color: CONFERENCE_COLOR }}>Upcoming </span> &amp; Current Events
        </h2>
      </div>

      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          border: "1px solid #ddd",
          padding: 20,
          borderRadius: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <button
            type="button"
            onClick={goPrev}
            style={{
              border: "1px solid #ddd",
              background: "#9B1321",
              padding: "8px 12px",
              color: "#fff",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Prev
          </button>

          <div style={{ fontWeight: 700 }}>
            {monthNames[activeDate.getMonth()]} {activeDate.getFullYear()}
          </div>

          <button
            type="button"
            onClick={goNext}
            style={{
              border: "1px solid #ddd",
              background: "#9B1321",
              padding: "8px 12px",
              color: "#fff",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Next
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 8,
            marginBottom: 8,
            color: "#666",
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          {weekDays.map((d) => (
            <div key={d} style={{ textAlign: "center" }}>
              {d}
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 12 }}>Loading...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
            {cells.map((dateObj, idx) => {
              if (!dateObj) return <div key={idx} style={{ height: 44, borderRadius: 10 }} />;

              const key = toISODateOnly(dateObj);
              const dayEvents = eventByDate.get(key) || [];
              const hasEvent = dayEvents.length > 0;

              const exhibitionEvent = dayEvents.find((ev) => ev.type === "exhibition");
              const conferenceEvent = dayEvents.find((ev) => ev.type === "conference");

              const displayEvent = exhibitionEvent || conferenceEvent || dayEvents[0];
              const cellColor = hasEvent ? getEventColor(displayEvent.type) : "#fff";

              return (
                <div
                  key={idx}
                  title={
                    hasEvent
                      ? dayEvents.map((ev) => `${ev.title} (${ev.type})`).join("\n")
                      : ""
                  }
                  onClick={() => hasEvent && onDayClick(dateObj)}
                  style={{
                    height: 44,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #eee",
                    background: cellColor,
                    color: hasEvent ? "#fff" : "#111",
                    fontWeight: hasEvent ? 700 : 600,
                    cursor: hasEvent ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  {dateObj.getDate()}
                </div>
              );
            })}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginTop: 16,
            justifyContent: "center",
            fontSize: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                background: EXHIBITION_COLOR,
                display: "inline-block",
              }}
            />
            <span>Exhibitions</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                background: CONFERENCE_COLOR,
                display: "inline-block",
              }}
            />
            <span>Conferences</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingCurrentEventsHeading;